import { Car, Clock, Users, ShieldCheck, ArrowUpRight } from "lucide-react";
import { waLink, tourMessage, trackWhatsApp } from "@/lib/whatsapp";
import { formatBRL } from "@/lib/tours";
import Reveal from "@/components/landing/Reveal";
import WhatsAppIcon from "@/components/landing/WhatsAppIcon";

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 border-t border-white/10 py-3.5">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</p>
      <p className="mt-0.5 text-sm text-stone-200">{value}</p>
    </div>
  </div>
);

export const BookingCard = ({ tour, location }) => (
  <aside
    data-testid="tour-booking-card"
    className="rounded-2xl border border-amber-400/25 bg-[#161B22] p-7 lg:sticky lg:top-28"
  >
    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-400">
      Valor por pessoa
    </p>
    <p className="mt-2 font-display text-4xl font-extrabold text-stone-50">
      R$ {formatBRL(tour.price)}
    </p>
    <p className="mt-1 text-sm text-stone-400">
      Valor mínimo da reserva: R$ {formatBRL(tour.min)} ({tour.minNote})
    </p>
    <div className="mt-6">
      <InfoRow icon={Clock} label="Duração" value={tour.duration} />
      <InfoRow icon={Users} label="Grupo" value="Pequenos grupos — consulte disponibilidade" />
      <InfoRow icon={Car} label="Transfer" value="Ida e volta da sua hospedagem" />
      <InfoRow icon={ShieldCheck} label="Guia" value="Credenciado · Cadastur 19.967376.16-7" />
    </div>
    <a
      href={waLink(tourMessage(tour.name))}
      onClick={() => trackWhatsApp(location, tour.name)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="tour-page-cta-card"
      className="group mt-6 flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:scale-[1.02] hover:bg-[#20BD5A]"
    >
      <WhatsAppIcon className="h-4 w-4" />
      Consultar e reservar
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </a>
    <p className="mt-4 text-center font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-stone-500">
      Reserva confirmada após o pagamento do sinal, combinado pelo WhatsApp.
    </p>
  </aside>
);

export default function TourOverview({ tour }) {
  return (
    <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
      <div className="grid gap-12 lg:grid-cols-[1fr_380px] lg:gap-16">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
            Sobre o passeio
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-stone-50 sm:text-4xl">
            Como é{" "}
            <em className="font-accent font-medium italic text-amber-400">esse dia.</em>
          </h2>
          <p data-testid="tour-page-description" className="mt-7 max-w-2xl text-base leading-relaxed text-stone-300 sm:text-lg">
            {tour.description}
          </p>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-stone-400 sm:text-base">
            Buscamos você na hospedagem e deixamos no mesmo local ao final do passeio. Se
            preferir descer em outro ponto, combine pelo WhatsApp na hora da reserva.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <BookingCard tour={tour} location="pagina_passeio_card" />
        </Reveal>
      </div>
    </section>
  );
}
